import ConsultationRichInput from '@brands/Components/Inputs/RichTextField/RichTextField';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { ICase } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { checkCollapsibleHeaderColor } from '@brands/Utils/getCollapsibleHeader';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import React, { useMemo, useState } from 'react';
import Modal, { Styles } from 'react-modal';

import { OralHealthReportSubmit } from '../../../assets/types';
import styles from './styles.module.scss';

type NoteModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (note: string) => void;
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  type: 'CASE_NOTE' | 'INTERNAL_CASE_NOTE';
};

const NoteModal = ({
  open,
  onClose,
  onSave,
  consultationForm,
  setConsultationForm,
  thisCase,
  type,
}: NoteModalProps) => {
  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);
  const { userInfo } = useAppSelector(selectAuth);

  const isInternal = type === 'INTERNAL_CASE_NOTE';

  const title = isInternal ? 'Add internal case note' : 'Add note';
  const submitText = isInternal ? 'Add internal case note' : 'Add note';
  const textFieldLabel = isInternal ? 'Add internal case notes here' : 'Add notes here';

  const mobileStyles: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...(customStylesDashboard.overlay || {}),
      overflowY: 'scroll',
    },
    content: {
      ...(customStylesDashboard.content || {}),
      width: '100vw',
      height: '100vh',
      padding: '90px 16px 32px 16px',
      borderRadius: '0',
    },
  };

  const desktopStyles: Styles = {
    ...customStylesDashboard,
    content: {
      ...customStylesDashboard.content,
      width: '444px',
      padding: '30px 32px 32px',
      maxHeight: '750px',
    },
  };

  const isReadOnly =
    checkCollapsibleHeaderColor(thisCase, userInfo, isInternal ? 'InternalNotes' : 'Notes') === 'readOnly';

  const [noteValue, setNoteValue] = useState('');

  const isNoteEmpty = (html: string): boolean => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text.length === 0;
  };

  const handleSave = () => {
    if (isNoteEmpty(noteValue)) return;

    onSave(noteValue);
    setNoteValue('');
    onClose();
  };

  return (
    <Modal isOpen={open} onRequestClose={onClose} ariaHideApp={false} style={isMobile ? mobileStyles : desktopStyles}>
      <div className={styles.NoteModal}>
        {/* Header */}
        <div className={styles.bodyHead}>
          <CloseIcon onClick={onClose} />
        </div>

        {/* Body */}
        <div className={`${styles.body} ${styles.formContainer}`}>
          <span className={styles.bodyHeader}>{title}</span>

          <div className={styles.bodySub}>
            <ConsultationRichInput
              value={noteValue}
              editable={!isReadOnly}
              placeholder={textFieldLabel}
              className={`fs-exclude ${styles.caseNotesInput}`}
              onChange={(html) => {
                setNoteValue(html);
                setConsultationForm((prev) => ({ ...prev, caseNotes: html }));
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={styles.bodyFooter}>
          {isInternal && (
            <span className={styles.internalNoteWarning}>Note: internal notes are not shared with the patient</span>
          )}

          <Button
            variant="contained"
            className={styles.containedBtn}
            onClick={handleSave}
            disabled={isReadOnly || isNoteEmpty(noteValue)}
          >
            {submitText}
          </Button>

          <Button variant="text" className={styles.textBtn} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NoteModal;
